import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ValidationResult } from '../../api/batch';
import { formatOU } from '../../utils/dn';
import type { PendingFile } from './use-batch-upload';

const PREVIEW_LIMIT = 20;

function NotFoundList({ upns }: { upns: string[] }) {
  const { t } = useTranslation();
  return (
    <div className="border border-coral/40 bg-coral/6 px-4 py-3">
      <p className="mb-1.5 text-xs font-medium text-coral">
        {t('batchUpload.notFoundList')}
      </p>
      <ul className="max-h-28 space-y-0.5 overflow-y-auto">
        {upns.map((upn) => (
          <li key={upn} className="font-mono text-xs text-coral">
            {upn}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PreviewTile({
  file,
  validation,
}: {
  file: PendingFile;
  validation: ValidationResult | undefined;
}) {
  const ou = formatOU(validation?.onPremisesDistinguishedName);
  const tooltip = [validation?.displayName, ou, file.upn]
    .filter(Boolean)
    .join('\n');
  return (
    <div className="flex flex-col items-center gap-1" title={tooltip}>
      <img
        src={file.previewUrl}
        alt={file.upn}
        className="size-14 border border-rule object-cover"
      />
      <span className="w-full truncate text-center text-xs text-muted">
        {validation?.displayName ?? file.upn}
      </span>
      {ou && (
        <span className="w-full truncate text-center text-xs text-muted-soft">
          {ou.split(' › ').at(-1)}
        </span>
      )}
    </div>
  );
}

function PreviewGrid({
  files,
  validationByUpn,
}: {
  files: PendingFile[];
  validationByUpn: Map<string, ValidationResult>;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-4 gap-3">
      {files.slice(0, PREVIEW_LIMIT).map((f) => (
        <PreviewTile
          key={f.upn}
          file={f}
          validation={validationByUpn.get(f.upn)}
        />
      ))}
      {files.length > PREVIEW_LIMIT && (
        <div className="flex flex-col items-center justify-center gap-1">
          <div className="flex size-14 items-center justify-center border border-dashed border-rule bg-ink/5">
            <span className="text-xs font-semibold text-muted">
              +{files.length - PREVIEW_LIMIT}
            </span>
          </div>
          <span className="text-xs text-muted-soft">
            {t('batchUpload.more')}
          </span>
        </div>
      )}
    </div>
  );
}

/* Confirmation salariés : la liste de qui partira en demande, et le rappel
   qu'un autre opérateur ou admin devra approuver. */
function EmployeeConfirm({
  employees,
  onConfirm,
  onCancel,
}: {
  employees: ValidationResult[];
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    // react-doctor-disable-next-line react-doctor/prefer-html-dialog -- an inline confirmation inside the upload panel, not an overlay: nothing to put in the top layer, and the page around it stays usable
    <div
      role="alertdialog"
      aria-label={t('batchUpload.employeeConfirm.title')}
      className="space-y-3 border border-blue bg-blue/6 px-4 py-3"
    >
      <p className="text-xs font-medium text-blue">
        {t('batchUpload.employeeConfirm.title')}
      </p>
      <p className="text-sm text-ink">
        {t('batchUpload.employeeConfirm.body', {
          count: employees.length,
        })}
      </p>
      <ul className="max-h-28 space-y-0.5 overflow-y-auto">
        {employees.map((v) => (
          <li key={v.upn} className="font-mono text-xs text-ink">
            {v.displayName ? `${v.displayName} — ${v.upn}` : v.upn}
          </li>
        ))}
      </ul>
      <div className="flex gap-3">
        <button type="button" onClick={onConfirm} className="btn-primary">
          {t('batchUpload.employeeConfirm.confirm')}
        </button>
        <button type="button" onClick={onCancel} className="btn-ghost">
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}

function UploadButton({
  count,
  onClick,
}: {
  count: number;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className="btn-primary w-full justify-center"
    >
      <svg
        className="size-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
        />
      </svg>
      {t('batchUpload.upload', { count })}
    </button>
  );
}

interface BatchUploadReadyProps {
  pending: PendingFile[];
  validation: ValidationResult[];
  confirmEmployeesOpen: boolean;
  setConfirmEmployeesOpen: (open: boolean) => void;
  onConfirmUpload: () => void;
  onReset: () => void;
}

/** Ready — validation results + confirm. */
export function BatchUploadReady({
  pending,
  validation,
  confirmEmployeesOpen,
  setConfirmEmployeesOpen,
  onConfirmUpload,
  onReset,
}: BatchUploadReadyProps) {
  const { t } = useTranslation();

  // One pass each instead of a .find() per element: with a big ZIP the
  // repeated scans were O(files × validation) on every render.
  const validationByUpn = useMemo(
    () => new Map(validation.map((v) => [v.upn, v])),
    [validation],
  );
  const pendingUpns = useMemo(
    () => new Set(pending.map((f) => f.upn)),
    [pending],
  );
  const foundFiles = pending.filter((f) => validationByUpn.get(f.upn)?.found);
  const notFoundUpns = validation.filter((v) => !v.found).map((v) => v.upn);
  // Les salariés (Lucca) du lot : leurs photos deviennent des demandes de
  // modération, pas des uploads directs — d'où la confirmation avant envoi.
  const employees = validation.filter(
    (v) => v.found && v.isEmployee && pendingUpns.has(v.upn),
  );

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="epi-tag green">
          {t('batchUpload.found', { count: foundFiles.length })}
        </span>
        {notFoundUpns.length > 0 && (
          <span className="epi-tag coral">
            {t('batchUpload.notFound', { count: notFoundUpns.length })}
          </span>
        )}
        <button
          type="button"
          onClick={onReset}
          className="ml-auto text-xs text-muted-soft transition-colors"
        >
          {t('common.cancel')}
        </button>
      </div>

      {/* Not-found list */}
      {notFoundUpns.length > 0 && <NotFoundList upns={notFoundUpns} />}

      {/* Found — photo preview grid */}
      {foundFiles.length > 0 && (
        <PreviewGrid files={foundFiles} validationByUpn={validationByUpn} />
      )}

      {confirmEmployeesOpen && employees.length > 0 && (
        <EmployeeConfirm
          employees={employees}
          onConfirm={onConfirmUpload}
          onCancel={() => setConfirmEmployeesOpen(false)}
        />
      )}

      {/* Confirm button */}
      {foundFiles.length > 0 && !confirmEmployeesOpen && (
        <UploadButton
          count={foundFiles.length}
          onClick={() => {
            if (employees.length > 0) {
              setConfirmEmployeesOpen(true);
            } else {
              onConfirmUpload();
            }
          }}
        />
      )}
      {foundFiles.length === 0 && (
        <p className="text-center text-sm text-muted">
          {t('batchUpload.noValidUsers')}
        </p>
      )}
    </div>
  );
}
